import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UtensilsCrossed,
  Stethoscope,
  Scissors,
  Building2,
  Hotel,
  Dumbbell,
  ShoppingBag,
  GraduationCap,
  Scale,
  ArrowRight,
} from "lucide-react";

const INDUSTRIES = [
  {
    id: "restaurants",
    icon: UtensilsCrossed,
    title: "restaurants",
    blurb: "reservation bots, review requests, and menu answers on whatsapp — bookings never miss a beat.",
  },
  {
    id: "clinics",
    icon: Stethoscope,
    title: "clinics",
    blurb: "book patients, send prep instructions, and cut no-shows with automated reminders.",
  },
  {
    id: "salons-spas",
    icon: Scissors,
    title: "salons & spas",
    blurb: "24/7 instagram booking, loyalty punch-cards, and re-book nudges after every visit.",
  },
  {
    id: "real-estate",
    icon: Building2,
    title: "real estate",
    blurb: "qualify buyers, schedule viewings, and send listings inside whatsapp automatically.",
  },
  {
    id: "hotels",
    icon: Hotel,
    title: "hotels",
    blurb: "answer availability, upsell rooms, and handle check-in prep before guests arrive.",
  },
  {
    id: "gyms",
    icon: Dumbbell,
    title: "gyms & fitness",
    blurb: "trial-class bookings, class reminders, and win-back campaigns for lapsed members.",
  },
  {
    id: "ecommerce",
    icon: ShoppingBag,
    title: "e-commerce",
    blurb: "abandoned-cart recovery on whatsapp, order updates, and support that never sleeps.",
  },
  {
    id: "schools",
    icon: GraduationCap,
    title: "schools",
    blurb: "answer admissions questions, book tours, and drip nurture parents till enrolment.",
  },
  {
    id: "law-firms",
    icon: Scale,
    title: "law firms",
    blurb: "intake bots that qualify cases and book consultations only when it fits your team.",
  },
];

export default function Industries() {
  return (
    <div className="bg-section-glow" data-testid="industries-page">
      <section className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-display lowercase font-bold text-5xl sm:text-6xl tracking-tighter" data-testid="industries-heading">
            if your customers message, <span className="text-blue-500">ai can help.</span>
          </h1>
          <p className="mt-5 text-gray-400 max-w-2xl">
            we adapt kyvixe to how your customers actually buy — from restaurants
            to law firms, we ship playbooks tuned to your industry.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INDUSTRIES.map((ind, i) => (
            <motion.a
              key={ind.id}
              href="/contact#contact"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="kyv-card p-7 block group"
              data-testid={`industry-card-${ind.id}`}
            >
              <div className="p-3 rounded-full bg-blue-500/10 text-blue-400 w-fit mb-4 group-hover:bg-blue-500/20 transition">
                <ind.icon size={22} />
              </div>
              <h3 className="font-display lowercase font-bold text-2xl tracking-tight">
                {ind.title}
              </h3>
              <p className="mt-3 text-sm text-gray-400 leading-relaxed">{ind.blurb}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-blue-400 text-sm">
                see how <ArrowRight size={14} />
              </span>
            </motion.a>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 text-center">
          <p className="font-display lowercase font-bold text-3xl tracking-tight">
            don't see your industry? <Link to="/contact" className="text-blue-500 hover:underline" data-testid="industries-contact-cta">let's talk.</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
